import type { ExportOptions, GenEdit, OrderOptions } from '@bkbnlab/api'
import { LabEngineRequest, LabEnhancementOptionsDTO, LabExportOptionsDTO, LabStagingEditDTO } from 'models/photoStudio'
import type { LabOrderOptions } from './labSdk.types'

/** The files a run starts from, already in the platform's temp bucket */
export interface LabEngineFiles {
  /** As save-temporary-visual named it */
  visualFilename: string
  /** Eraser only: the mask, as mask-upload-url named it */
  maskFilename?: string
  /** Eraser only: the object detection of this same `visualFilename`, whose upload to the Lab is reused */
  objectsJobId?: string
}

/** SDK wire value → platform enum (`low_cloud` → `LOW_CLOUD`); the type pins every SDK value to the backend enum */
const toEnum = <T extends string>(value: T): Uppercase<T> => value.toUpperCase() as Uppercase<T>
const toOptionalEnum = <T extends string>(value: T | undefined): Uppercase<T> | undefined =>
  value === undefined ? undefined : toEnum(value)

const STAGING_EDITS: Partial<Record<GenEdit, LabStagingEditDTO>> = {
  indoor_staging: 'INDOOR_STAGING',
  outdoor_staging: 'OUTDOOR_STAGING',
  declutter_local: 'DECLUTTER',
  mess_cleaner: 'MESS_CLEANER',
}

/** An order carries Enhance operations — on its own, or merged into a generative edit by Combine tools */
const hasEnhancement = ({ perspective, tonecraft, privacy, sky }: OrderOptions): boolean =>
  Boolean(perspective || tonecraft || privacy || sky)

const toEnhancementOptions = (order: OrderOptions): LabEnhancementOptionsDTO => {
  const { perspective, tonecraft, privacy, sky } = order
  const tonecraftOptions = typeof tonecraft === 'object' ? tonecraft : undefined
  return {
    perspective: Boolean(perspective),
    perspectiveStrength: typeof perspective === 'object' ? perspective.strength : undefined,
    tonecraft: Boolean(tonecraft),
    tonecraftProfile: typeof tonecraft === 'string' ? tonecraft : tonecraftOptions?.profile,
    tonecraftLevels: tonecraftOptions?.levels,
    privacy: Boolean(privacy),
    privacyCategories: typeof privacy === 'object' ? privacy.categories : undefined,
    skyStyle: typeof sky === 'string' ? toEnum(sky) : undefined,
  }
}

/** Every tool's result is delivered as the run asks — the Image format of Enhance, JPEG otherwise */
const toExportOptions = ({ format, quality }: ExportOptions): LabExportOptionsDTO => ({
  format: toEnum(format),
  quality: format === 'png' ? undefined : quality,
})

/**
 * The platform request for an order the panels describe in Lab SDK terms (`labSdk.mapping.ts`), so the panels and
 * their mapping stay as they are. Asset ids in the order are ignored: the platform hands the Lab its own uploads.
 * Stage and Erase carry a Combine tools run's Enhance operations as `enhancement`: one Lab order, effects stacked.
 */
export const toLabEngineRequest = (order: LabOrderOptions, exportOptions: ExportOptions, files: LabEngineFiles): LabEngineRequest => {
  const { visualFilename } = files
  const stacked = hasEnhancement(order) ? { enhancement: toEnhancementOptions(order) } : {}
  const delivery = toExportOptions(exportOptions)

  if (order.genEdit === 'magic_eraser') {
    if (!files.maskFilename) throw new Error('lab_eraser_missing_mask')
    return {
      tool: 'eraser',
      body: { visualFilename, maskFilename: files.maskFilename, objectsJobId: files.objectsJobId, ...stacked, ...delivery },
    }
  }

  const edit = order.genEdit && STAGING_EDITS[order.genEdit]
  if (order.genEdit && !edit) throw new Error(`lab_unsupported_edit: ${order.genEdit}`)
  if (edit) {
    return {
      tool: 'staging',
      body: {
        visualFilename,
        edit,
        stagingStyle: toOptionalEnum(order.stagingStyle),
        styleClause: order.styleClause,
        roomType: toOptionalEnum(order.roomType),
        floorFinish: toOptionalEnum(order.floorFinish),
        wallColor: toOptionalEnum(order.wallColor),
        ceilingLight: toOptionalEnum(order.ceilingLight),
        generationQuality: toOptionalEnum(order.genQuality),
        ...stacked,
        ...delivery,
      },
    }
  }

  return { tool: 'enhancement', body: { ...toEnhancementOptions(order), ...delivery, visualFilename } }
}

/**
 * A photo an object detection already put in the temp bucket (click-to-select in the eraser), reused by the erase that
 * follows: the eraser must name the detection of this very upload, so the two travel together.
 */
export interface LabDetectedSource {
  visualFilename: string
  objectsJobId: string
}
